import type { BaseCmsImageProps } from "src/components/images/BaseCmsImageProps";
import { getCmsImageAttrs } from "src/components/images/imageFunctions";
import styles from "./CmsImageForAssetRef.module.css";
import { preload } from "react-dom";
import type { ImageHotspot } from 'sanity';
import classNames from "classnames";

const BRAND = process.env.NEXT_PUBLIC_WEBSITE_BRAND;

//#region [Props]
type CmsImageFromAssetRefProps = {
    assetRef: string;
    hotspot?: ImageHotspot;
} & BaseCmsImageProps;

//#endregion

//#region [Component]
/**
 * A plain <img> straight from Sanity's CDN. Loads lazily unless the page sets priority, which it should only do
 * for the page's main (LCP) image: then it loads eagerly with high priority and is preloaded from <head>.
 */
export default function CmsImageForAssetRef({ assetRef, alt, className, sizes, width, height, priority = false, loading, unoptimized = false, crop = false, isPosterAspect = false, nonPosterAspect, hotspot, applyAspectRatio = false, style }: CmsImageFromAssetRefProps) {
    if (!assetRef || !assetRef.startsWith("image-")) {
        return null;
    }
    const img = getCmsImageAttrs({ assetRef, width, height, sizes, unoptimized, crop, isPosterAspect, nonPosterAspect, hotspot });

    if (priority) {
        // Must use the same srcset and sizes as the <img>, or the browser downloads the image twice
        preload(img.src, { as: "image", imageSrcSet: img.srcSet, imageSizes: img.sizes, fetchPriority: "high" });
    }

    let _alt = alt;
    if (BRAND === "kinodk" && _alt?.startsWith("Bilde fra ")) {
        _alt = _alt.replace("Bilde fra ", "Billede fra ")
    }
    // src is last: Safari starts fetching src as soon as it is set, before srcSet and sizes (same as next/image)
    return <img alt={_alt}
        className={classNames(className, { [styles.applyAspectRatio]: applyAspectRatio })}
        // color: transparent hides the alt text while the image loads, like next/image
        style={{ color: "transparent", ...(applyAspectRatio ? { '--custom-aspect-ratio': `${img.width} / ${img.height}` } : {}), ...style } as React.CSSProperties}
        width={img.width}
        height={img.height}
        loading={priority ? "eager" : loading ?? "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
        sizes={img.sizes}
        srcSet={img.srcSet}
        src={img.src}
    />
}
//#endregion
